import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { FaBars, FaSignOutAlt } from "react-icons/fa";
import { MdSpaceDashboard, MdPayments } from "react-icons/md";
import { GiRuleBook } from "react-icons/gi";
import { GrAnnounce } from "react-icons/gr";
import { AiFillProfile } from "react-icons/ai";
import { AiFillSchedule } from "react-icons/ai";
import { FaCalendarTimes } from "react-icons/fa";
import { FaMoneyBillWave } from "react-icons/fa";
import { FaBalanceScale } from "react-icons/fa";
import { MdMenuBook } from "react-icons/md";
import { FaClock } from "react-icons/fa";
import { IoSettingsSharp } from "react-icons/io5";
import { IoCalendarSharp } from "react-icons/io5";
import { HiDocumentReport } from "react-icons/hi";
import { HiMiniUserGroup } from "react-icons/hi2";
import { GrDocumentPerformance } from "react-icons/gr";
import { MdCoPresent } from "react-icons/md";
import { FaRegGrinBeam } from "react-icons/fa";
import { RiRefund2Fill } from "react-icons/ri";
import { IoDocument } from "react-icons/io5";
import axios from 'axios';
import './sidebar.css';

const iconMap = {
  MdSpaceDashboard: <MdSpaceDashboard />,
  GiRuleBook: <GiRuleBook />,
  GrAnnounce: <GrAnnounce />,
  AiFillProfile: <AiFillProfile />,
  MdPayments: <MdPayments />,
  AiFillSchedule: <AiFillSchedule />,
  FaCalendarTimes: <FaCalendarTimes />,
  FaMoneyBillWave: <FaMoneyBillWave />,
  FaBalanceScale: <FaBalanceScale />,
  MdMenuBook: <MdMenuBook />,
  FaClock: <FaClock />,
  IoSettingsSharp: <IoSettingsSharp />,
  IoCalendarSharp: <IoCalendarSharp />,
  HiDocumentReport: <HiDocumentReport />,
  HiMiniUserGroup: <HiMiniUserGroup />,
  GrDocumentPerformance: <GrDocumentPerformance />,
  MdCoPresent: <MdCoPresent />,
  FaRegGrinBeam: <FaRegGrinBeam />,
  RiRefund2Fill: <RiRefund2Fill />,
  IoDocument: <IoDocument />,
};

const PRESENTATION_PAGE_NAME = 'Presentation';
const LEAVE_MANAGER_PATH = '/leavemanager';

const ALLOWED_FOR_PRESENTATION = ['Manager', 'Team Leader'];
const ALLOWED_FOR_LEAVE_MANAGER = ['Manager'];

// ✅ Expense module access — only for AM-36 (User ID: 20)
const EXPENSE_ALLOWED_USERNAMES = ['am-36'];
const EXPENSE_ALLOWED_USER_IDS   = ['20'];

const isExpenseAllowedUser = () => {
  // 🔍 DEBUG: Print all localStorage keys to see exact variable names
  console.log("📦 FULL LOCAL STORAGE:", { ...localStorage });

  const userId = String(
    localStorage.getItem('userId') ||
    localStorage.getItem('user_id') ||
    localStorage.getItem('id') ||
    localStorage.getItem('employeeId') ||
    localStorage.getItem('empId') ||
    ''
  ).trim();

  const userName = String(
    localStorage.getItem('name') ||
    localStorage.getItem('username') ||
    localStorage.getItem('userName') ||
    localStorage.getItem('employeeCode') ||
    localStorage.getItem('empCode') ||
    ''
  ).trim().toLowerCase();

  const isAllowed = EXPENSE_ALLOWED_USER_IDS.includes(userId) || EXPENSE_ALLOWED_USERNAMES.includes(userName);
  console.log(`🔍 Expense Check -> ID: "${userId}", Name: "${userName}", Allowed: ${isAllowed}`);

  return isAllowed;
};

const isExpenseRoute = (route) => {
  const p = String(route.path || '').toLowerCase();
  const n = String(route.name || '').toLowerCase();
  return p.includes('expense') || n.includes('expense');
};

const SideBar = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [userDepartmentType, setUserDepartmentType] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let rawDeptType = localStorage.getItem('departmentType');
    const deptType = rawDeptType ? rawDeptType.trim() : null;

    setUserDepartmentType(deptType || 'Employee');

    axios.get('https://backend.ems.hiradentallab.com/api/panel-pages/panel/EmployeePanel')
      .then(response => {
        let fetchedRoutes = response.data.map(pp => ({
          id: pp._id,
          path: pp.page.path,
          name: pp.page.name,
          icon: iconMap[pp.page.icon] || <AiFillProfile />,
          subRoutes: pp.page.subRoutes || [],
          isPresentation: pp.page.name === PRESENTATION_PAGE_NAME,
          isLeaveManager: pp.page.path === LEAVE_MANAGER_PATH,
        }));

        // ✅ MANUAL INJECTION: If API didn't return Expenses for AM-36, add it manually
        if (isExpenseAllowedUser()) {
          const hasExpense = fetchedRoutes.some(r => isExpenseRoute(r));
          if (!hasExpense) {
            console.log("⚠️ API did not return Expense page for AM-36. Injecting manually...");
            fetchedRoutes.push({
              id: 'manual-expense',
              path: '/expense-info',
              name: 'Expenses',
              icon: <MdPayments />,
              subRoutes: [],
              isPresentation: false,
              isLeaveManager: false,
            });
          }
        }

        const filteredRoutes = fetchedRoutes.filter(route => {
          if (route.isPresentation) {
            if (!deptType || !ALLOWED_FOR_PRESENTATION.includes(deptType)) return false;
          }

          if (route.isLeaveManager) {
            if (!deptType || !ALLOWED_FOR_LEAVE_MANAGER.includes(deptType)) return false;
          }

          // ✅ EXPENSE MODULE — only for AM-36
          if (isExpenseRoute(route) && !isExpenseAllowedUser()) {
            console.log(`❌ Hiding Expenses for ${localStorage.getItem('name')}`);
            return false;
          }

          return true;
        });

        setRoutes(filteredRoutes);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error:', error);
        setRoutes([]);
        setLoading(false);
      });
  }, []);

  const toggleSidebar = () => setIsOpen(!isOpen);
  const handleLinkClick = () => { if (window.innerWidth <= 1024) setIsOpen(false); };
  const handleLogout = () => { localStorage.clear(); navigate('/'); };

  const isActive = (path) => {
    const route = routes.find(r => r.path === path);
    if (!route) return location.pathname.startsWith(path);
    const hasActiveSub = route.subRoutes.some(sub => location.pathname.startsWith(sub.path));
    return location.pathname.startsWith(path) || hasActiveSub;
  };

  if (loading) return <div className="sidebar-loading">Loading menu...</div>;

  return (
    <>
      <div className="sidebar-container">
        <button className="hamburger" onClick={toggleSidebar}><FaBars /></button>
        <div className={`sidebar ${isOpen ? 'open' : ''}`}>
          <div className="top_section">
            <h1 className="logo">EMS</h1>
            <div className="user-info">
              <div className="user-name">{localStorage.getItem('name') || 'User'}</div>
              <div className="user-dept-type">{userDepartmentType}</div>
            </div>
          </div>
          <section className="routes">
            {routes.length > 0 ? routes.map((route, index) => (
              <NavLink
                to={route.path}
                key={index}
                className={`link ${isActive(route.path) ? 'active' : ''}`}
                onClick={handleLinkClick}
              >
                <div className="icon">{route.icon}</div>
                <div className={`link_text ${isOpen ? "visible" : "hidden"}`}>
                  {route.name}
                </div>
              </NavLink>
            )) : <div className="no-routes">No menu items</div>}
          </section>
          <div className="sidebar-footer">
            <button className="logout-btn" onClick={handleLogout} title="Logout">
              <div className="icon"><FaSignOutAlt /></div>
              <div className={`link_text ${isOpen ? "visible" : "hidden"}`}>Logout</div>
            </button>
          </div>
        </div>
      </div>
      <main>{children}</main>
    </>
  );
};

export default SideBar;